import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Plus, Zap, XCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function OffersTab() {
  const { t } = useTranslation();
  const [offers, setOffers] = useState(null);
  const [boostingId, setBoostingId] = useState(null);
  const [boostPrice, setBoostPrice] = useState(null);
  const [freeBoostAvailable, setFreeBoostAvailable] = useState(false);

  const loadOffers = () => api.get("/offers/mine").then(({ data }) => setOffers(data));

  useEffect(() => {
    loadOffers();
    api.get("/payments/packages").then(({ data }) => setBoostPrice(data.boost_price));
    api.get("/profiles/producer/me").then(({ data }) => setFreeBoostAvailable(!data.free_boost_used));
  }, []);

  const handleClose = async (offerId) => {
    await api.post(`/offers/${offerId}/close`);
    loadOffers();
  };

  const handleBoost = async (offerId) => {
    setBoostingId(offerId);
    try {
      const { data } = await api.post("/payments/checkout/boost", { offer_id: offerId, origin_url: window.location.origin });
      if (data.free) {
        toast.success(t("offer.freeBoostApplied"));
        setBoostingId(null);
        setFreeBoostAvailable(false);
        loadOffers();
      } else {
        window.location.href = data.url;
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail, t));
      setBoostingId(null);
    }
  };

  if (!offers) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#4ba3b7]" /></div>;

  return (
    <div data-testid="producer-offers-tab">
      <div className="flex justify-end mb-6">
        <Button asChild className="h-11 rounded-xl bg-[#4ba3b7] hover:bg-[#3D8A9A] font-bold gap-2" data-testid="producer-create-offer-button">
          <Link to="/dashboard/producer/offers/new"><Plus className="h-4 w-4" /> {t("nav.postOffer")}</Link>
        </Button>
      </div>

      {offers.length === 0 ? (
        <p className="text-center py-16 text-[#2B2B2B]/50" data-testid="producer-offers-empty-message">{t("search.noResults")}</p>
      ) : (
        <div className="space-y-4">
          {offers.map((offer) => (
            <div key={offer.id} className="bg-white rounded-2xl border border-black/5 shadow-[0_4px_24px_rgba(0,0,0,0.03)] p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid={`producer-offer-row-${offer.id}`}>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-heading font-bold">{t(`lookups.missionTypes.${offer.mission_type}`)}</h3>
                  <Badge variant="outline" className="rounded-full text-xs">{t(`offer.status${offer.status.charAt(0).toUpperCase() + offer.status.slice(1)}`)}</Badge>
                  {offer.is_boosted && <Badge className="bg-[#dde165]/25 text-[#2B2B2B] border-0 rounded-full text-xs">{t("common.boosted")}</Badge>}
                </div>
                <p className="text-sm text-[#2B2B2B]/60">
                  {t(`lookups.regions.${offer.region}`)} &middot; {offer.applications_count || 0} {t("offer.applicationsReceived")}
                </p>
              </div>
              <div className="flex gap-2">
                <Button asChild variant="outline" size="sm" className="rounded-lg" data-testid={`producer-offer-view-applicants-${offer.id}`}>
                  <Link to={`/dashboard/producer/applications?offer_id=${offer.id}`}>{t("application.viewApplicants")}</Link>
                </Button>
                {!offer.is_boosted && offer.status === "active" && (
                  <Button size="sm" variant="outline" onClick={() => handleBoost(offer.id)} disabled={boostingId === offer.id}
                          className="rounded-lg gap-1 border-[#dde165] text-[#2B2B2B]" data-testid={`producer-offer-boost-button-${offer.id}`}>
                    <Zap className="h-3.5 w-3.5" /> {t("offer.boostThisOffer")}
                    {freeBoostAvailable ? (
                      <Badge className="ml-1 bg-[#34C759]/15 text-[#34C759] border-0 rounded-full text-[10px]" data-testid={`producer-offer-boost-free-badge-${offer.id}`}>
                        {t("offer.freeBoostBadge")}
                      </Badge>
                    ) : (boostPrice ? ` — ${boostPrice.price}€` : "")}
                  </Button>
                )}
                {offer.status === "active" && (
                  <Button size="sm" variant="ghost" onClick={() => handleClose(offer.id)} className="rounded-lg text-[#FF3B30] gap-1" data-testid={`producer-offer-close-button-${offer.id}`}>
                    <XCircle className="h-3.5 w-3.5" /> {t("offer.closeOffer")}
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
